import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, Eye, EyeOff, Loader2, HelpCircle, X as XIcon, UserPlus, MessageCircleQuestion, BadgeCheck, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

// ---------- Validation helpers -------------------------------------------
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PROVINCE_RE = /^[A-Za-z]{2}$/;

function validateName(v) {
    if (!v.trim()) return "Campo obbligatorio";
    if (v.trim().length < 2) return "Inserisci almeno 2 caratteri";
    if (!/\s/.test(v.trim())) return "Inserisci nome e cognome";
    return "";
}
function validateEmail(v) {
    if (!v.trim()) return "Campo obbligatorio";
    if (!EMAIL_RE.test(v.trim())) return "Email non valida";
    return "";
}
function validateCity(v) {
    if (!v.trim()) return "Campo obbligatorio";
    if (v.trim().length < 2) return "Città non valida";
    return "";
}
function validateProvince(v) {
    if (!v.trim()) return "Campo obbligatorio";
    if (!PROVINCE_RE.test(v.trim())) return "Sigla di 2 lettere (es. MI)";
    return "";
}
function passwordChecks(v) {
    return {
        length: v.length >= 8,
        letterAndDigit: /[A-Za-z]/.test(v) && /\d/.test(v),
    };
}
function validatePassword(v) {
    if (!v) return "Campo obbligatorio";
    const c = passwordChecks(v);
    if (!c.length) return "La password deve contenere almeno 8 caratteri";
    if (!c.letterAndDigit) return "La password deve contenere almeno 1 lettera e 1 numero";
    return "";
}

// ---------- Reusable field wrapper --------------------------------------
function FieldError({ show, message, testid }) {
    if (!show || !message) return null;
    return (
        <p
            id={testid}
            data-testid={testid}
            className="text-xs text-rose-600 font-medium flex items-center gap-1 mt-1"
            role="alert"
        >
            <XIcon className="size-3.5" aria-hidden />
            {message}
        </p>
    );
}

// ---------- Password checklist -----------------------------------------
function PasswordChecklist({ value }) {
    const c = passwordChecks(value);
    const Row = ({ ok, label, testid }) => (
        <li
            data-testid={testid}
            className={`flex items-center gap-2 text-xs transition ${
                ok ? "text-emerald-700" : "text-slate-500"
            }`}
        >
            <span
                aria-hidden
                className={`inline-flex size-4 rounded-full items-center justify-center border ${
                    ok
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "bg-white border-slate-300 text-slate-300"
                }`}
            >
                {ok ? <Check className="size-3" /> : <span className="block size-1.5 rounded-full bg-slate-300" />}
            </span>
            {label}
        </li>
    );
    return (
        <ul
            data-testid="register-password-checklist"
            className="space-y-1 mt-2"
            aria-label="Requisiti password"
        >
            <Row
                ok={c.length}
                label="Almeno 8 caratteri"
                testid="register-pwd-req-length"
            />
            <Row
                ok={c.letterAndDigit}
                label="Almeno 1 lettera e 1 numero"
                testid="register-pwd-req-mix"
            />
        </ul>
    );
}

// ---------- Main component ---------------------------------------------
export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [city, setCity] = useState("");
    const [province, setProvince] = useState("");
    const [password, setPassword] = useState("");
    const [consent, setConsent] = useState(false);

    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState("");

    // Per-field "touched" state — errors are only revealed once the user
    // interacts with a field (or attempts to submit) to avoid a hostile UI
    // that yells "required!" on an empty page.
    const [touched, setTouched] = useState({});
    const [attempted, setAttempted] = useState(false);
    const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));

    const errors = useMemo(
        () => ({
            fullName: validateName(fullName),
            email: validateEmail(email),
            city: validateCity(city),
            province: validateProvince(province),
            password: validatePassword(password),
            consent: consent ? "" : "Devi accettare i termini per continuare",
        }),
        [fullName, email, city, province, password, consent],
    );
    const isValid = Object.values(errors).every((e) => !e);
    const show = (k) => (touched[k] || attempted) && errors[k];

    const submit = async (e) => {
        e.preventDefault();
        setAttempted(true);
        setServerError("");
        if (!isValid) {
            // Move focus to first invalid field for accessibility
            const order = ["fullName", "email", "city", "province", "password", "consent"];
            const firstBad = order.find((k) => errors[k]);
            if (firstBad) {
                const el = document.getElementById(
                    firstBad === "fullName" ? "fullName" :
                    firstBad === "consent" ? "consent" : firstBad,
                );
                el?.focus?.();
            }
            return;
        }
        setLoading(true);
        try {
            const res = await register({
                full_name: fullName,
                email,
                city: city.trim(),
                province: province.trim().toUpperCase(),
                password,
            });
            navigate("/verifica-email", {
                state: {
                    email: res?.email || email,
                    fresh: true,
                    email_sent: res?.email_sent !== false,
                    verification_url: res?.verification_url,
                },
            });
        } catch (err) {
            setServerError(apiError(err));
        } finally {
            setLoading(false);
        }
    };

    const inputClass = (k, extra = "") =>
        `${show(k) ? "border-rose-400 focus-visible:ring-rose-300" : ""} ${extra}`.trim();

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-start justify-center bg-slate-50 px-4 py-14 sm:py-20">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center hover:opacity-80">
                        <img src="/brand-header.png" alt="CondominioNow" className="h-[60px] w-auto object-contain" />
                    </Link>
                </div>
                <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="space-y-4 pb-6">
                        <h1 className="font-serif text-3xl font-semibold">Crea il tuo account</h1>

                        {/* Premium info card — presentazione visiva dei 4 punti chiave */}
                        <div
                            data-testid="register-info-card"
                            className="relative rounded-2xl border-2 border-[#3083C9]/15 bg-gradient-to-br from-[#3083C9]/5 via-white to-[#F59547]/5 shadow-md shadow-[#3083C9]/5 p-5 sm:p-6"
                        >
                            <ul className="space-y-3.5">
                                {/* Registrati */}
                                <li className="flex items-start gap-3" data-testid="register-info-item-1">
                                    <span
                                        aria-hidden
                                        className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#3083C9]/10 text-[#3083C9]"
                                    >
                                        <UserPlus className="size-5" />
                                    </span>
                                    <span className="pt-1.5 text-sm font-medium text-slate-700 leading-tight">
                                        Registrati
                                    </span>
                                </li>

                                {/* Fai la tua domanda */}
                                <li className="flex items-start gap-3" data-testid="register-info-item-2">
                                    <span
                                        aria-hidden
                                        className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#3083C9]/10 text-[#3083C9]"
                                    >
                                        <MessageCircleQuestion className="size-5" />
                                    </span>
                                    <span className="pt-1.5 text-sm font-medium text-slate-700 leading-tight">
                                        Fai la tua domanda
                                    </span>
                                </li>

                                {/* Il servizio è gratuito — enfasi */}
                                <li className="flex items-start gap-3" data-testid="register-info-item-3">
                                    <span
                                        aria-hidden
                                        className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#F59547]/15 text-[#F59547]"
                                    >
                                        <BadgeCheck className="size-5" />
                                    </span>
                                    <span className="pt-1.5 text-sm font-bold text-[#F59547] leading-tight">
                                        Il servizio è gratuito
                                    </span>
                                </li>

                                {/* Privacy garantita — enfasi + dettaglio */}
                                <li className="flex items-start gap-3" data-testid="register-info-item-4">
                                    <span
                                        aria-hidden
                                        className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#3083C9]/15 text-[#3083C9]"
                                    >
                                        <ShieldCheck className="size-5" />
                                    </span>
                                    <span className="text-sm leading-relaxed">
                                        <span className="block font-bold text-[#3083C9]">
                                            Privacy garantita:
                                        </span>
                                        <span className="block text-xs text-slate-600 mt-0.5">
                                            le tue domande restano riservate. Solo tu e il professionista che le prende in carico possono leggerle.
                                        </span>
                                    </span>
                                </li>
                            </ul>
                        </div>

                        <Link
                            to="/come-funziona"
                            data-testid="register-come-funziona"
                            className="inline-flex items-center gap-1.5 self-start text-sm font-semibold text-primary hover:text-primary/80 hover:underline underline-offset-4 transition"
                        >
                            <HelpCircle className="size-4" /> Come funziona?
                        </Link>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={submit} className="space-y-4" data-testid="register-form" noValidate>
                            {serverError && (
                                <Alert variant="destructive" data-testid="register-error">
                                    <AlertDescription>{serverError}</AlertDescription>
                                </Alert>
                            )}

                            {/* Nome */}
                            <div className="space-y-1.5">
                                <Label htmlFor="fullName">Nome e cognome</Label>
                                <Input
                                    id="fullName"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    onBlur={() => touch("fullName")}
                                    data-testid="register-name"
                                    placeholder="Mario Rossi"
                                    aria-invalid={Boolean(show("fullName"))}
                                    aria-describedby="err-fullName"
                                    className={inputClass("fullName")}
                                />
                                <FieldError show={show("fullName")} message={errors.fullName} testid="err-fullName" />
                            </div>

                            {/* Email */}
                            <div className="space-y-1.5">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={() => touch("email")}
                                    data-testid="register-email"
                                    placeholder="tuo@email.it"
                                    aria-invalid={Boolean(show("email"))}
                                    aria-describedby="err-email"
                                    className={inputClass("email")}
                                />
                                <FieldError show={show("email")} message={errors.email} testid="err-email" />
                            </div>

                            {/* Città + Provincia */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="space-y-1.5 col-span-2">
                                    <Label htmlFor="city">Città</Label>
                                    <Input
                                        id="city"
                                        autoComplete="address-level2"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        onBlur={() => touch("city")}
                                        data-testid="register-city"
                                        aria-invalid={Boolean(show("city"))}
                                        aria-describedby="err-city"
                                        className={inputClass("city")}
                                    />
                                    <FieldError show={show("city")} message={errors.city} testid="err-city" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="province">Provincia</Label>
                                    <Input
                                        id="province"
                                        autoComplete="address-level1"
                                        value={province}
                                        onChange={(e) => setProvince(e.target.value.toUpperCase().slice(0, 2))}
                                        onBlur={() => touch("province")}
                                        data-testid="register-province"
                                        maxLength={2}
                                        aria-invalid={Boolean(show("province"))}
                                        aria-describedby="err-province"
                                        className={inputClass("province", "uppercase")}
                                    />
                                    <FieldError show={show("province")} message={errors.province} testid="err-province" />
                                </div>
                            </div>

                            {/* Password */}
                            <div className="space-y-1.5">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPwd ? "text" : "password"}
                                        autoComplete="new-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onBlur={() => touch("password")}
                                        data-testid="register-password"
                                        placeholder="Almeno 8 caratteri"
                                        aria-invalid={Boolean(show("password"))}
                                        aria-describedby="err-password register-password-checklist"
                                        className={inputClass("password", "pr-10")}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd(!showPwd)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                                    >
                                        {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                    </button>
                                </div>
                                <FieldError show={show("password")} message={errors.password} testid="err-password" />
                                <PasswordChecklist value={password} />
                            </div>

                            {/* Consenso */}
                            <div className="pt-2">
                                <div className="flex items-start gap-3">
                                    <Checkbox
                                        id="consent"
                                        checked={consent}
                                        onCheckedChange={(v) => { setConsent(Boolean(v)); touch("consent"); }}
                                        data-testid="register-consent"
                                        className={`mt-0.5 ${show("consent") ? "border-rose-400" : ""}`}
                                    />
                                    <Label
                                        htmlFor="consent"
                                        className="text-sm font-normal text-muted-foreground leading-relaxed cursor-pointer"
                                    >
                                        Ho letto e accetto la Privacy Policy e i Termini di servizio.
                                    </Label>
                                </div>
                                <FieldError show={show("consent")} message={errors.consent} testid="err-consent" />
                            </div>

                            <Button
                                data-testid="register-submit"
                                type="submit"
                                className="w-full h-11"
                                disabled={loading}
                                aria-disabled={!isValid || loading}
                            >
                                {loading && <Loader2 className="size-4 mr-2 animate-spin" />}
                                {loading ? "Creazione account…" : "Crea account"}
                            </Button>
                            {attempted && !isValid && !loading && (
                                <p
                                    data-testid="register-form-summary-error"
                                    className="text-xs text-rose-600 text-center"
                                    role="status"
                                >
                                    Correggi i campi evidenziati per continuare.
                                </p>
                            )}
                            <div className="text-center text-sm text-muted-foreground pt-2">
                                Hai già un account?{" "}
                                <Link
                                    to="/login"
                                    className="text-primary font-semibold hover:underline"
                                    data-testid="register-to-login"
                                >
                                    Accedi
                                </Link>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
